import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { COURSE_CONFIG } from '../types';

export interface CourseCertificateConfig {
  name: string;
  displayNameOnCertificate: string;
  completionStatement: string;
  complianceStatement?: string;
  validityMonths: number;
  templateType: string;
  requiredFields: string[];
  defaultInstructor?: string;
  additionalStaticText?: string[];
  imagePlaceholder?: string;
}

/** Aliases so DB "Safe Pass" shares config with static "Safepass Training". */
export const COURSE_NAME_ALIASES: Record<string, string[]> = {
  'Safe Pass': ['Safepass Training', 'SafePass', 'Safepass'],
  'Safepass Training': ['Safe Pass', 'SafePass', 'Safepass'],
};

export function courseNameCandidates(name: string): string[] {
  const n = name.trim();
  const aliases = COURSE_NAME_ALIASES[n] || [];
  return [n, ...aliases.filter((a) => a !== n)];
}

function mapRow(row: any): CourseCertificateConfig {
  const required = Array.isArray(row.required_fields)
    ? row.required_fields.map(String)
    : typeof row.required_fields === 'string'
      ? (() => {
          try {
            return JSON.parse(row.required_fields);
          } catch {
            return [];
          }
        })()
      : [];
  const extra = Array.isArray(row.additional_static_text)
    ? row.additional_static_text.map(String)
    : [];
  return {
    name: row.name,
    displayNameOnCertificate: row.display_name_on_certificate || row.name,
    completionStatement: row.completion_statement || '',
    complianceStatement: row.compliance_statement || undefined,
    validityMonths: Number(row.validity_months) || 36,
    templateType: row.template_type || 'GENERIC',
    requiredFields: required,
    defaultInstructor: row.default_instructor || '',
    additionalStaticText: extra.length ? extra : undefined,
    imagePlaceholder: row.image_placeholder || undefined,
  };
}

function fromStatic(name: string): CourseCertificateConfig | null {
  for (const candidate of courseNameCandidates(name)) {
    const cfg = COURSE_CONFIG[candidate as keyof typeof COURSE_CONFIG] as
      | CourseCertificateConfig
      | undefined;
    if (cfg) {
      return {
        name,
        displayNameOnCertificate: cfg.displayNameOnCertificate,
        completionStatement: cfg.completionStatement,
        complianceStatement: cfg.complianceStatement,
        validityMonths: cfg.validityMonths,
        templateType: cfg.templateType,
        requiredFields: [...(cfg.requiredFields || [])],
        defaultInstructor: cfg.defaultInstructor || '',
        additionalStaticText: cfg.additionalStaticText,
        imagePlaceholder: (cfg as any).imagePlaceholder,
      };
    }
  }
  return null;
}

export const courseConfigurationService = {
  async list(): Promise<CourseCertificateConfig[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('certificate_course_configs')
      .select('*')
      .order('name');
    if (error) {
      console.error('certificate_course_configs list:', error);
      return [];
    }
    return (data || []).map(mapRow);
  },

  async getByName(name: string): Promise<CourseCertificateConfig | null> {
    if (!isSupabaseConfigured()) return fromStatic(name);

    const candidates = courseNameCandidates(name);
    const { data, error } = await supabase
      .from('certificate_course_configs')
      .select('*')
      .in('name', candidates)
      .limit(5);
    if (error) {
      console.error('certificate_course_configs getByName:', error);
      return fromStatic(name);
    }
    if (data?.length) {
      // Prefer exact name match
      const exact = data.find((r) => r.name === name) || data[0];
      return mapRow(exact);
    }
    return fromStatic(name);
  },

  async upsert(config: CourseCertificateConfig): Promise<void> {
    if (!isSupabaseConfigured()) throw new Error('Database is not configured');
    const payload = {
      name: config.name.trim(),
      display_name_on_certificate: config.displayNameOnCertificate,
      completion_statement: config.completionStatement,
      compliance_statement: config.complianceStatement || null,
      validity_months: config.validityMonths,
      template_type: config.templateType,
      required_fields: config.requiredFields || [],
      default_instructor: config.defaultInstructor || '',
      additional_static_text: config.additionalStaticText || [],
      image_placeholder: config.imagePlaceholder || null,
      updated_at: new Date().toISOString(),
    };
    const { error } = await supabase.from('certificate_course_configs').upsert(payload, {
      onConflict: 'name',
    });
    if (error) throw error;
  },

  /** Seed missing rows from static COURSE_CONFIG (idempotent). */
  async seedFromStaticConfig(): Promise<number> {
    if (!isSupabaseConfigured()) return 0;
    const existing = await this.list();
    const have = new Set(existing.map((c) => c.name));
    let inserted = 0;
    for (const [name, cfg] of Object.entries(COURSE_CONFIG)) {
      if (have.has(name)) continue;
      try {
        await this.upsert({
          name,
          displayNameOnCertificate: cfg.displayNameOnCertificate,
          completionStatement: cfg.completionStatement,
          complianceStatement: cfg.complianceStatement,
          validityMonths: cfg.validityMonths,
          templateType: cfg.templateType,
          requiredFields: [...(cfg.requiredFields || [])],
          defaultInstructor: cfg.defaultInstructor || '',
          additionalStaticText: cfg.additionalStaticText,
          imagePlaceholder: (cfg as any).imagePlaceholder,
        });
        inserted += 1;
      } catch (e) {
        console.warn('Failed to seed course config', name, e);
      }
    }
    // Ensure Safe Pass alias row exists pointing at Safepass Training content
    if (!have.has('Safe Pass')) {
      const sp = fromStatic('Safe Pass');
      if (sp) {
        try {
          await this.upsert({ ...sp, name: 'Safe Pass' });
          inserted += 1;
        } catch {
          /* ignore */
        }
      }
    }
    return inserted;
  },
};
